/**
 * FlashcardFocusMode
 *
 * A modal component for focused flashcard review in messages
 *
 * Primary responsibilities:
 * - Display flashcards in a full-screen modal view with darkened background
 * - Provide navigation between multiple flashcards with next/prev buttons
 * - Track and display score for correct/incorrect answers
 * - Support keyboard navigation
 */

import React, { useState, useEffect, useCallback } from 'react'
import { Dialog, DialogContent, DialogOverlay } from '@/components/ui/dialog'
import { Flashcard, ConfidenceLevel } from '@/components/flashcard/flashcard'
import { Button } from '@/components/ui/button'
import {
  IconArrowLeft,
  IconArrowRight,
  IconCheck,
  IconX,
  IconCards,
  IconRefresh
} from '@tabler/icons-react'
import { cn } from '@/lib/utils'

export interface FlashcardItem {
  question: string
  answer: string
}

interface FlashcardFocusModeProps {
  flashcards: FlashcardItem[]
  isOpen: boolean
  onOpenChange: (isOpen: boolean) => void
}

// Custom DialogOverlay with enhanced blur effect
const BlurredDialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogOverlay>,
  React.ComponentPropsWithoutRef<typeof DialogOverlay>
>(({ className, ...props }, ref) => (
  <DialogOverlay
    ref={ref}
    className={cn(
      'bg-background/40 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 grid overflow-auto p-4 backdrop-blur-xl sm:place-items-center',
      className
    )}
    {...props}
  />
))
BlurredDialogOverlay.displayName = 'BlurredDialogOverlay'

export const FlashcardFocusMode: React.FC<FlashcardFocusModeProps> = ({
  flashcards,
  isOpen,
  onOpenChange
}) => {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [scoreCorrect, setScoreCorrect] = useState(0)
  const [scoreIncorrect, setScoreIncorrect] = useState(0)
  const [reviewedCards, setReviewedCards] = useState<Set<number>>(new Set())
  const [cardConfidences, setCardConfidences] = useState<Record<number, ConfidenceLevel>>({})
  const [flippedStates, setFlippedStates] = useState<Record<number, boolean>>({})

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setCurrentIndex(0)
      setScoreCorrect(0)
      setScoreIncorrect(0)
      setReviewedCards(new Set())
      setCardConfidences({})
      setFlippedStates({})
    }
  }, [isOpen])

  const handlePrevious = useCallback(() => {
    // Reset the current card to not flipped
    setFlippedStates((prev) => ({
      ...prev,
      [currentIndex]: false
    }))

    // Navigate to previous card
    setCurrentIndex((prev) => {
      const newIndex = Math.max(0, prev - 1)
      return newIndex
    })
  }, [currentIndex])

  const handleNext = useCallback(() => {
    // Reset the current card to not flipped
    setFlippedStates((prev) => ({
      ...prev,
      [currentIndex]: false
    }))

    // Navigate to next card
    setCurrentIndex((prev) => {
      const newIndex = Math.min(flashcards.length - 1, prev + 1)
      return newIndex
    })
  }, [currentIndex, flashcards.length])

  const handleConfidenceChange = useCallback(
    (confidence: ConfidenceLevel) => {
      if (confidence === 'correct' && !reviewedCards.has(currentIndex)) {
        setScoreCorrect((prev) => prev + 1)
        setReviewedCards((prev) => new Set(prev).add(currentIndex))
      } else if (confidence === 'incorrect' && !reviewedCards.has(currentIndex)) {
        setScoreIncorrect((prev) => prev + 1)
        setReviewedCards((prev) => new Set(prev).add(currentIndex))
      }

      setCardConfidences((prev) => ({
        ...prev,
        [currentIndex]: confidence
      }))
    },
    [currentIndex, reviewedCards]
  )

  const handleCorrect = useCallback(() => {
    handleConfidenceChange('correct')
  }, [handleConfidenceChange])

  const handleIncorrect = useCallback(() => {
    handleConfidenceChange('incorrect')
  }, [handleConfidenceChange])

  // Track flip state changes
  const handleFlipChange = useCallback(
    (isFlipped: boolean) => {
      setFlippedStates((prev) => ({
        ...prev,
        [currentIndex]: isFlipped
      }))
    },
    [currentIndex]
  )

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return

    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowLeft':
          handlePrevious()
          break
        case 'ArrowRight':
          handleNext()
          break
        case 'Escape':
          onOpenChange(false)
          break
        case '1':
        case 'j':
          handleCorrect()
          break
        case '0':
        case 'k':
          handleIncorrect()
          break
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, handlePrevious, handleNext, onOpenChange, handleCorrect, handleIncorrect])

  // Add a function to reset/restart the session
  const handleRestart = useCallback(() => {
    setCurrentIndex(0)
    setScoreCorrect(0)
    setScoreIncorrect(0)
    setReviewedCards(new Set())
    setCardConfidences({})
    setFlippedStates({})
  }, [])

  if (!flashcards.length) return null

  const currentFlashcard = flashcards[currentIndex]
  const totalReviewed = scoreCorrect + scoreIncorrect
  const progress = totalReviewed > 0 ? Math.round((scoreCorrect / totalReviewed) * 100) : 0
  const isCurrentCardFlipped = flippedStates[currentIndex] || false

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <BlurredDialogOverlay />
      <DialogContent
        className="bg-background/95 flex h-[80vh] max-w-4xl flex-col overflow-hidden border-none p-0 shadow-xl backdrop-blur-2xl"
        forceMount
      >
        {/* Fixed position buttons in top-right corner */}
        <div className="absolute right-4 top-4 z-10 flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleRestart}
            className="border-border/50 bg-background/80 hover:bg-background/90 flex h-8 items-center gap-1 rounded-full border backdrop-blur-sm"
          >
            <IconRefresh size={14} />
            <span>Try Again</span>
          </Button>
          <Button
            variant="outline"
            size="icon"
            onClick={() => onOpenChange(false)}
            className="border-border/50 bg-background/80 hover:bg-background/90 size-8 rounded-full border backdrop-blur-sm"
          >
            <IconX size={16} />
            <span className="sr-only">Exit</span>
          </Button>
        </div>

        <div className="flex items-center justify-between border-b p-4">
          <div className="flex items-center gap-2">
            <IconCards size={18} className="text-muted-foreground" />
            <h2 className="text-lg font-semibold">Flashcard Review</h2>
          </div>

          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="flex items-center font-medium text-green-600 dark:text-green-400">
                <IconCheck size={16} className="mr-1" />
                {scoreCorrect}
              </span>
              <span className="flex items-center font-medium text-red-600 dark:text-red-400">
                <IconX size={16} className="mr-1" />
                {scoreIncorrect}
              </span>
            </div>

            <div className="flex items-center gap-1">
              <span className="text-sm">
                {currentIndex + 1}/{flashcards.length}
              </span>
              <div className="h-2 w-24 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
                <div
                  className="h-full bg-blue-500 transition-all duration-300"
                  style={{ width: `${((currentIndex + 1) / flashcards.length) * 100}%` }}
                />
              </div>
            </div>

            <div className="text-sm font-medium">Score: {progress}%</div>
          </div>
        </div>

        <div className="flex flex-1 items-center justify-center p-6">
          <div className="w-full max-w-3xl">
            <Flashcard
              id={`focus-card-${currentIndex}`}
              question={currentFlashcard.question}
              answer={currentFlashcard.answer}
              initialConfidence={cardConfidences[currentIndex] || null}
              onConfidenceChange={handleConfidenceChange}
              forceInitialFlip={isCurrentCardFlipped}
              onFlipChange={handleFlipChange}
            />
          </div>
        </div>

        <div className="flex items-center justify-between border-t p-4">
          <Button variant="outline" onClick={handlePrevious} disabled={currentIndex === 0}>
            <IconArrowLeft size={16} className="mr-2" />
            Previous
          </Button>

          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={handleIncorrect}
              className="border-red-200 bg-red-100 text-red-700 hover:bg-red-200 dark:border-red-800/30 dark:bg-red-900/20 dark:text-red-400 dark:hover:bg-red-900/30"
            >
              <IconX size={16} className="mr-2" />
              Missed
            </Button>

            <Button
              variant="outline"
              onClick={handleCorrect}
              className="border-green-200 bg-green-100 text-green-700 hover:bg-green-200 dark:border-green-800/30 dark:bg-green-900/20 dark:text-green-400 dark:hover:bg-green-900/30"
            >
              <IconCheck size={16} className="mr-2" />
              Correct
            </Button>
          </div>

          <Button
            variant="outline"
            onClick={handleNext}
            disabled={currentIndex === flashcards.length - 1}
          >
            Next
            <IconArrowRight size={16} className="ml-2" />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
